class ProductRelatedRecentlyViewed extends HTMLElement {
  constructor() {
    super();

    this.handleTabClick = this.handleTabClick.bind(this);
    this.handleTabKeydown = this.handleTabKeydown.bind(this);
  }

  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.sectionId = this.dataset.sectionId;
    this.currentHandle = this.dataset.currentProductHandle;
    this.storageKey = this.dataset.storageKey || 'amerce_recently_viewed_products';
    this.recentLimit = Math.max(1, Number(this.dataset.recentLimit) || 4);
    this.view = this.dataset.view || 'carousel';
    this.itemDesktop = Math.max(1, Number(this.dataset.itemDesktop) || 4);
    this.itemXl = Math.max(1, Number(this.dataset.itemXl) || this.itemDesktop);
    this.itemTablet = Math.max(1, Number(this.dataset.itemTablet) || 3);
    this.itemMobileSm = Math.max(1, Number(this.dataset.itemMobileSm) || 2);
    this.itemMobile = Math.max(1, Number(this.dataset.itemMobile) || 2);
    this.space = Math.max(0, Number(this.dataset.space) || 20);
    this.paginationStyle = this.dataset.paginationStyle || 'bullet';

    this.tabs = Array.from(this.querySelectorAll('[data-products-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-products-panel]'));
    this.recentContent = this.querySelector('[data-recent-content]');

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.handleTabClick);
      tab.addEventListener('keydown', this.handleTabKeydown);
    });

    this.previousHandles = this.readRecentlyViewed().filter(
      (handle) => handle && handle !== this.currentHandle,
    );

    this.saveCurrentProduct();
    this.loadRelatedProducts();

    const activeTab = this.querySelector('[data-products-tab][aria-selected="true"]');
    if (activeTab?.dataset.productsTab === 'recently-viewed') {
      this.loadRecentlyViewed();
    }
  }

  disconnectedCallback() {
    this.tabs?.forEach((tab) => {
      tab.removeEventListener('click', this.handleTabClick);
      tab.removeEventListener('keydown', this.handleTabKeydown);
    });

    this.relatedController?.abort();
    this.recentController?.abort();
  }

  handleTabClick(event) {
    this.activateTab(event.currentTarget, true);
  }

  handleTabKeydown(event) {
    const currentIndex = this.tabs.indexOf(event.currentTarget);
    let nextIndex = currentIndex;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % this.tabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.tabs[nextIndex].focus();
    this.activateTab(this.tabs[nextIndex], true);
  }

  activateTab(tab, loadContent = false) {
    const target = tab.dataset.productsTab;

    this.tabs.forEach((item) => {
      const isActive = item === tab;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-selected', String(isActive));
      item.tabIndex = isActive ? 0 : -1;
    });

    this.panels.forEach((panel) => {
      const isActive = panel.dataset.productsPanel === target;
      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
    });

    if (loadContent && target === 'recently-viewed') {
      this.loadRecentlyViewed();
    }

    const activePanel = this.panels.find((panel) => panel.dataset.productsPanel === target);
    this.updateSwiper(activePanel);
  }

  async loadRelatedProducts() {
    if (this.dataset.recommendationsLoaded === 'true') return;

    const url = this.dataset.recommendationsUrl;
    const content = this.querySelector('[data-related-content]');
    if (!url || !content) return;

    this.relatedController?.abort();
    this.relatedController = new AbortController();

    try {
      const response = await fetch(url, {
        signal: this.relatedController.signal,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);

      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const fetchedContent = documentFragment.querySelector('[data-related-content]');

      if (!fetchedContent) throw new Error('Recommendations markup was not found.');

      content.replaceChildren(...Array.from(fetchedContent.childNodes).map((node) => node.cloneNode(true)));
      this.dataset.recommendationsLoaded = 'true';
      this.notifyCardsLoaded('related', content);
      this.updateSwiper(content);
    } catch (error) {
      if (error.name === 'AbortError') return;
      content.innerHTML = `<p class="product-related-recently-viewed__error">${this.escapeHtml(this.dataset.loadError || '')}</p>`;
      console.error(error);
    }
  }

  async loadRecentlyViewed() {
    if (this.dataset.recentLoaded === 'true' || this.dataset.recentLoading === 'true') return;
    if (!this.recentContent) return;

    this.dataset.recentLoading = 'true';
    const handles = this.previousHandles.slice(0, this.recentLimit);

    if (!handles.length) {
      this.renderRecentEmpty();
      this.dataset.recentLoaded = 'true';
      this.dataset.recentLoading = 'false';
      return;
    }

    this.recentController?.abort();
    this.recentController = new AbortController();

    try {
      const cards = await Promise.all(
        handles.map((handle) => this.fetchRecentlyViewedCard(handle, this.recentController.signal)),
      );

      const validCards = cards.filter(Boolean);
      if (!validCards.length) {
        this.renderRecentEmpty();
      } else {
        const layout = this.createProductsLayout(validCards);
        this.recentContent.replaceChildren(layout);
        this.notifyCardsLoaded('recently-viewed', layout);
        this.updateSwiper(this.recentContent);
      }

      this.dataset.recentLoaded = 'true';
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.recentContent.innerHTML = `<p class="product-related-recently-viewed__error">${this.escapeHtml(this.dataset.loadError || '')}</p>`;
        console.error(error);
      }
    } finally {
      this.dataset.recentLoading = 'false';
    }
  }


  createProductsLayout(cards) {
    if (this.view === 'carousel') {
      return this.createCarousel(cards);
    }

    return this.createGrid(cards);
  }

  createGrid(cards) {
    const list = document.createElement('ul');
    list.className = [
      'product-related-recently-viewed__grid',
      'row-grid',
      `xxl-col-${this.itemDesktop}`,
      `xl-col-${this.itemXl}`,
      `lg-col-${this.itemTablet}`,
      `md-col-${this.itemMobileSm}`,
      `sm-col-${this.itemMobile}`,
    ].join(' ');
    list.setAttribute('role', 'list');
    list.style.setProperty('--grid-gap-lg', `${this.space}px`);
    list.style.setProperty('--grid-gap-md', `${Math.min(this.space, 20)}px`);
    list.style.setProperty('--grid-gap-mobile', `${Math.min(this.space, 15)}px`);

    cards.forEach((card) => {
      card.classList.remove('swiper-slide');
      list.append(card);
    });

    return list;
  }

  createCarousel(cards) {
    const carousel = document.createElement('tf-swiper');
    carousel.className = 'product-related-recently-viewed__carousel';

    const swiper = document.createElement('div');
    swiper.className = 'swiper tf-swiper swiper-init';
    swiper.dataset.previewXl = String(this.itemDesktop);
    swiper.dataset.preview = String(this.itemXl);
    swiper.dataset.tablet = String(this.itemTablet);
    swiper.dataset.mobileSm = String(this.itemMobileSm);
    swiper.dataset.mobile = String(this.itemMobile);
    swiper.dataset.spaceLg = String(this.space);
    swiper.dataset.spaceMd = String(Math.min(this.space, 20));
    swiper.dataset.space = String(Math.min(this.space, 15));
    swiper.dataset.paginationLg = String(cards.length > this.itemDesktop);
    swiper.dataset.paginationMd = String(cards.length > this.itemTablet);
    swiper.dataset.pagination = String(cards.length > this.itemMobile);

    const wrapper = document.createElement('ul');
    wrapper.className = 'swiper-wrapper';
    wrapper.setAttribute('role', 'list');

    cards.forEach((card) => {
      card.classList.add('swiper-slide');
      wrapper.append(card);
    });

    swiper.append(wrapper);

    if (cards.length > this.itemMobile) {
      const pagination = document.createElement('div');
      pagination.className = `swiper-pagination${this.paginationStyle === 'bar' ? ' pagination-bar' : ''}`;
      swiper.append(pagination);
    }

    carousel.append(swiper);
    return carousel;
  }

  updateSwiper(container) {
    if (!container) return;

    requestAnimationFrame(() => {
      container.querySelectorAll('.swiper').forEach((element) => {
        element.swiper?.update?.();
      });

      window.dispatchEvent(
        new CustomEvent('theme:swiper:update', {
          detail: { container, sectionId: this.sectionId },
        }),
      );
    });
  }

  async fetchRecentlyViewedCard(handle, signal) {
    const root = this.dataset.productRoot || `${window.Shopify?.routes?.root || '/'}products/`;
    const sectionUrl = new URL(`${root}${encodeURIComponent(handle)}`, window.location.origin);
    sectionUrl.searchParams.set('section_id', this.sectionId);

    let response = await fetch(sectionUrl.toString(), {
      signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });

    if (response.ok) {
      const sectionHtml = await response.text();
      const card = this.extractCardTemplate(sectionHtml, handle);
      if (card) return card;
    }

    const productUrl = new URL(`${root}${encodeURIComponent(handle)}`, window.location.origin);
    response = await fetch(productUrl.toString(), {
      signal,
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });

    if (!response.ok) return null;
    return this.extractCardTemplate(await response.text(), handle);
  }

  extractCardTemplate(html, handle) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const templates = Array.from(parsed.querySelectorAll('template[data-recently-viewed-card-template]'));
    const template = templates.find((item) => item.dataset.productHandle === handle) || templates[0];

    if (!template) return null;

    const item = template.content.querySelector('.product-related-recently-viewed__item');
    return item ? item.cloneNode(true) : null;
  }

  renderRecentEmpty() {
    this.recentContent.innerHTML = `<p class="product-related-recently-viewed__empty">${this.escapeHtml(this.dataset.recentEmpty || '')}</p>`;
  }

  notifyCardsLoaded(source, container) {
    const detail = { source, sectionId: this.sectionId, container };
    this.dispatchEvent(new CustomEvent('product-cards:loaded', { bubbles: true, detail }));
    window.dispatchEvent(new CustomEvent('theme:product-cards:loaded', { detail }));
  }

  readRecentlyViewed() {
    try {
      const stored = JSON.parse(localStorage.getItem(this.storageKey) || '[]');
      if (!Array.isArray(stored)) return [];

      return stored
        .map((entry) => (typeof entry === 'string' ? entry : entry?.handle))
        .filter((handle) => typeof handle === 'string' && handle.length > 0);
    } catch (_) {
      return [];
    }
  }

  saveCurrentProduct() {
    if (!this.currentHandle) return;

    const handles = [
      this.currentHandle,
      ...this.readRecentlyViewed().filter((handle) => handle !== this.currentHandle),
    ].slice(0, 20);

    try {
      localStorage.setItem(this.storageKey, JSON.stringify(handles));
    } catch (_) {
      // Browsing can continue when storage is unavailable.
    }
  }

  escapeHtml(value) {
    const element = document.createElement('div');
    element.textContent = value || '';
    return element.innerHTML;
  }
}

if (!customElements.get('product-related-recently-viewed')) {
  customElements.define('product-related-recently-viewed', ProductRelatedRecentlyViewed);
}
